import React, { useState } from 'react';
import { Card, Button } from '../../ui';
import { DocumentCheckIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

const ProjectForm = ({
  project,
  isNewProject,
  saving,
  assigningHumanAdmin,
  revokingHumanAdmin,
  onInputChange,
  onSwitchChange,
  onSave,
  onAssignHumanAdmin,
  onRevokeHumanAdmin,
}) => {
  const [showBotToken, setShowBotToken] = useState(isNewProject);

  const hasProvisioning = Boolean(project?.bot_token && project?.report_group_id);
  const hasAiManagedGroup = (
    !isNewProject &&
    project?.provisioning_status === 'auto' &&
    Boolean(project?.owner_account_id && project?.report_group_id)
  );
  const brandAnalyticsThemeIds = Array.isArray(project?.brand_analytics_theme_ids)
    ? project.brand_analytics_theme_ids.join(', ')
    : (project?.brand_analytics_theme_ids || '');

  return (
    <Card className="mb-6">
      <h2 className="text-lg font-medium text-gray-900 mb-4">Project Information</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
            Project Name
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={project.name}
            onChange={onInputChange}
            className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            required
          />
        </div>

        <div className="md:col-span-2">
          <label htmlFor="activity_description" className="block text-sm font-medium text-gray-700 mb-1">
            Activity matching description
          </label>
          <textarea
            id="activity_description"
            name="activity_description"
            value={project.activity_description || ''}
            onChange={onInputChange}
            rows={3}
            maxLength={2000}
            placeholder="What work belongs to this project, its products, audience, and common activity context"
            className="block w-full resize-y border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
          />
          <p className="mt-1 text-xs text-gray-500">
            Used together with the project name and first- and second-order keywords after an activity chain closes.
          </p>
        </div>

        <div className="relative">
          <label htmlFor="bot_token" className="block text-sm font-medium text-gray-700 mb-1">
            Telegram Bot Token
          </label>
          <input
            type={showBotToken ? 'text' : 'password'}
            id="bot_token"
            name="bot_token"
            value={project.bot_token}
            onChange={onInputChange}
            className="block w-full border border-gray-300 rounded-md shadow-sm py-2 pl-3 pr-24 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            required={!hasProvisioning}
          />
          <button
            type="button"
            onClick={() => setShowBotToken((value) => !value)}
            className="absolute right-2 top-8 inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900"
            aria-label={showBotToken ? 'Hide bot token' : 'Show bot token'}
          >
            {showBotToken ? (
              <EyeSlashIcon className="h-4 w-4" />
            ) : (
              <EyeIcon className="h-4 w-4" />
            )}
            {showBotToken ? 'Hide' : 'Show'}
          </button>
          <p className="mt-1 text-xs text-gray-500">
            {hasProvisioning
              ? 'Auto-provisioned. Token is managed by the farm account that owns the bot.'
              : 'Token from @BotFather'}
          </p>
        </div>

        <div>
          <label htmlFor="report_group_id" className="block text-sm font-medium text-gray-700 mb-1">
            Report Group ID
          </label>
          <input
            type="text"
            id="report_group_id"
            name="report_group_id"
            value={project.report_group_id}
            onChange={onInputChange}
            className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            required={!hasProvisioning}
          />
          <p className="mt-1 text-xs text-gray-500">
                      Group ID where messages will be forwarded
                      {project.report_topic_id ? (
                        <>
                          {' '}— topic <span className="font-mono">#{project.report_topic_id}</span>
                        </>
                      ) : null}
                    </p>
                    {project.report_group_id && project.report_topic_id && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        <a
                          href={`https://t.me/c/${String(project.report_group_id).replace('-100', '')}/${project.report_topic_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-2 py-1 text-xs rounded bg-primary-50 text-primary-700 hover:bg-primary-100 border border-primary-200"
                        >
                          Open topic
                        </a>
                        {project.invite_link && (
                          <a
                            href={project.invite_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center px-2 py-1 text-xs rounded bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200"
                          >
                            Invite link
                          </a>
                        )}
                      </div>
                    )}
                    {project.invite_link && !project.report_topic_id && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        <a
                          href={project.invite_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-2 py-1 text-xs rounded bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200"
                        >
                          Join via invite link
                        </a>
                      </div>
                    )}
                  </div>

        <div className="md:col-span-2 rounded-md border border-gray-200 bg-gray-50 p-4">
          <label htmlFor="brand_analytics_theme_ids" className="block text-sm font-medium text-gray-700 mb-1">
            Brand Analytics theme IDs
          </label>
          <input
            type="text"
            id="brand_analytics_theme_ids"
            name="brand_analytics_theme_ids"
            value={brandAnalyticsThemeIds}
            onChange={onInputChange}
            placeholder="14114393, 14114394"
            className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
          />
          <p className="mt-1 text-xs text-gray-500">
            Comma-separated Brand Analytics projects/themes whose messages belong to this TeleFarm project.
          </p>
          <div className="mt-3 flex items-start">
            <input
              type="checkbox"
              id="brand_analytics_auto_join"
              name="brand_analytics_auto_join"
              checked={project.brand_analytics_auto_join === true}
              onChange={onSwitchChange}
              className="mt-0.5 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="brand_analytics_auto_join" className="ml-2 block text-sm text-gray-900">
              Enqueue missing public Telegram groups for monitoring-account subscription
              <span className="block text-xs font-normal text-gray-500">
                Uses the existing guarded group-join capacity and rate-limit controls. Disabled by default.
              </span>
            </label>
          </div>
        </div>

        {hasAiManagedGroup && (
          <div>
            <label htmlFor="human_admin_user_id" className="block text-sm font-medium text-gray-700 mb-1">
              Human Administrator Telegram User ID
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                id="human_admin_user_id"
                name="human_admin_user_id"
                value={project.human_admin_user_id || ''}
                onChange={onInputChange}
                placeholder="123456789"
                className="min-w-0 flex-1 border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              />
              <button
                type="button"
                onClick={onAssignHumanAdmin}
                disabled={assigningHumanAdmin || !project.human_admin_user_id}
                className="inline-flex items-center justify-center rounded-md border border-primary-600 bg-primary-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {assigningHumanAdmin ? 'Assigning...' : 'Assign admin'}
              </button>
              {project.human_admin_user_id && (
                <button
                  type="button"
                  onClick={onRevokeHumanAdmin}
                  disabled={assigningHumanAdmin || revokingHumanAdmin}
                  className="inline-flex items-center justify-center rounded-md border border-red-300 bg-white px-3 py-2 text-sm font-medium text-red-700 shadow-sm hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {revokingHumanAdmin ? 'Revoking...' : 'Revoke managed admin'}
                </button>
              )}
            </div>
            <p className="mt-1 text-xs text-gray-500">
              The user must join this project group first. Assignment grants rights to manage the chat,
              delete messages, and invite users. To replace a different managed ID, revoke the current
              assignment first so TeleFarm can restore its prior Telegram status safely.
            </p>
          </div>
        )}

        <div className="flex items-center mt-6">
          <input
            type="checkbox"
            id="is_active"
            name="is_active"
            checked={project.is_active}
            onChange={onSwitchChange}
            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
          />
          <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
            Active
          </label>
        </div>
      </div>

      <div className="mt-6 flex justify-end">
        <Button
          variant="primary"
          icon={DocumentCheckIcon}
          onClick={onSave}
          isLoading={saving}
        >
          {saving ? 'Saving...' : 'Save Project'}
        </Button>
      </div>

    </Card>
  );
};

export default ProjectForm;
